import type { EditCrop, EditFormat, EditLook, EditMotion, EditTransform, FilterType } from '@airpost/shared';

/**
 * How a piece is painted, as plain numbers the renderer applies: its colour (a canvas filter, a tint and a lift over
 * the picture, a vignette), and where its picture sits in the frame (the crop, the fit, the journalist's frame and the
 * slow move). Pure, so the monitor and the export paint alike and the tests can read it.
 */
interface Grade {
  grayscale: number;
  sepia: number;
  saturate: number;
  contrast: number;
  brightness: number;
  /** −1 cool … 1 warm. */
  warm: number;
  /** A white veil (the faded look), 0..1. */
  lift: number;
  vignette: number;
}
const IDENTITY: Grade = { grayscale: 0, sepia: 0, saturate: 1, contrast: 1, brightness: 1, warm: 0, lift: 0, vignette: 0 };
/** Each filter at full strength. */
export const FILTER_GRADES: Record<FilterType, Partial<Grade>> = {
  none: {},
  bw: { grayscale: 1, contrast: 1.1 },
  vivid: { saturate: 1.45, contrast: 1.08 },
  warm: { warm: 0.6, saturate: 1.1, sepia: 0.12 },
  cool: { warm: -0.6, saturate: 0.95 },
  fade: { contrast: 0.82, saturate: 0.75, lift: 0.12, brightness: 1.04 },
  cinematic: { contrast: 1.18, saturate: 0.85, warm: -0.2, vignette: 0.35 },
  vintage: { sepia: 0.45, contrast: 0.9, saturate: 0.85, lift: 0.06, vignette: 0.4 },
  noir: { grayscale: 1, contrast: 1.5, brightness: 0.88, vignette: 0.5 },
  dramatic: { contrast: 1.35, saturate: 1.15, brightness: 0.94, vignette: 0.25 },
};

export interface LookPaint {
  /** The canvas filter (`none` when nothing changes). */
  css: string;
  /** Colours laid thinly over the picture only (a warm or cool tint, the faded look's lift). */
  washes: Array<{ color: string; alpha: number }>;
  /** 0..1, over the whole frame of the piece. */
  vignette: number;
}

const r2 = (x: number) => Math.round(x * 1000) / 1000;

/** A look as paint: the filter at its strength, then the adjustments; `unit` scales the blur to the frame (1 at 720). */
export function lookPaint(look: EditLook, unit = 1): LookPaint {
  const preset = FILTER_GRADES[look.filter] ?? {};
  const g = { ...IDENTITY };
  for (const k of Object.keys(IDENTITY) as Array<keyof Grade>) {
    const target = preset[k];
    if (target !== undefined) g[k] = IDENTITY[k] + (target - IDENTITY[k]) * look.amount;
  }
  g.brightness *= 1 + 0.5 * look.brightness;
  g.contrast *= 1 + 0.6 * look.contrast;
  g.saturate *= 1 + look.saturation;
  g.warm = Math.max(-1, Math.min(1, g.warm + look.temperature));
  const vignette = Math.min(1, g.vignette + look.vignette);
  const parts: string[] = [];
  if (Math.abs(g.brightness - 1) > 0.001) parts.push(`brightness(${r2(g.brightness)})`);
  if (Math.abs(g.contrast - 1) > 0.001) parts.push(`contrast(${r2(g.contrast)})`);
  if (Math.abs(g.saturate - 1) > 0.001) parts.push(`saturate(${r2(Math.max(0, g.saturate))})`);
  if (g.grayscale > 0.001) parts.push(`grayscale(${r2(g.grayscale)})`);
  if (g.sepia > 0.001) parts.push(`sepia(${r2(g.sepia)})`);
  if (look.blur > 0.001) parts.push(`blur(${r2(look.blur * 12 * unit)}px)`);
  const washes: LookPaint['washes'] = [];
  if (g.warm > 0.001) washes.push({ color: '#ff9a3c', alpha: r2(0.16 * g.warm) });
  if (g.warm < -0.001) washes.push({ color: '#3c8cff', alpha: r2(0.16 * -g.warm) });
  if (g.lift > 0.001) washes.push({ color: '#ffffff', alpha: r2(g.lift) });
  return { css: parts.length ? parts.join(' ') : 'none', washes, vignette: r2(vignette) };
}

/** A slow move at `q` (0..1) through its piece: a size over the frame and a shift of the centre, fractions of the frame. */
export function motionAt(m: EditMotion, q: number): { scale: number; dx: number; dy: number } {
  const a = m.amount;
  const t = Math.min(1, Math.max(0, q));
  switch (m.type) {
    case 'zoom-in':
      return { scale: 1 + a * t, dx: 0, dy: 0 };
    case 'zoom-out':
      return { scale: 1 + a * (1 - t), dx: 0, dy: 0 };
    // a pan is drawn a little larger than the frame, so its edges never show
    case 'pan-left':
      return { scale: 1 + a, dx: a / 2 - a * t, dy: 0 };
    case 'pan-right':
      return { scale: 1 + a, dx: -a / 2 + a * t, dy: 0 };
    case 'pan-up':
      return { scale: 1 + a, dx: 0, dy: a / 2 - a * t };
    case 'pan-down':
      return { scale: 1 + a, dx: 0, dy: -a / 2 + a * t };
    default:
      return { scale: 1, dx: 0, dy: 0 };
  }
}

export interface Placement {
  /** The part of the source drawn (after the crop). */
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  /** Its centre in the frame, its size there, its turn (radians) and mirror. */
  cx: number;
  cy: number;
  dw: number;
  dh: number;
  rotation: number;
  flipH: boolean;
  flipV: boolean;
}

/** Where a picture of `iw`×`ih` goes in a `w`×`h` frame: cropped, fitted (inside or covering), then the journalist's frame and the move. */
export function placePicture(iw: number, ih: number, w: number, h: number, fit: EditFormat['fit'], t: Pick<EditTransform, 'scale' | 'x' | 'y' | 'rotation' | 'flipH' | 'flipV'> & { crop: EditCrop }, move = { scale: 1, dx: 0, dy: 0 }): Placement {
  const sx = t.crop.left * iw;
  const sy = t.crop.top * ih;
  const sw = Math.max(1, iw * (1 - t.crop.left - t.crop.right));
  const sh = Math.max(1, ih * (1 - t.crop.top - t.crop.bottom));
  const base = fit === 'cover' ? Math.max(w / sw, h / sh) : Math.min(w / sw, h / sh);
  const s = base * t.scale * move.scale;
  return { sx, sy, sw, sh, cx: w / 2 + (t.x + move.dx) * w, cy: h / 2 + (t.y + move.dy) * h, dw: sw * s, dh: sh * s, rotation: (t.rotation * Math.PI) / 180, flipH: t.flipH, flipV: t.flipV };
}

/** A number from a frame's index (for grain, shake and glitch that are the same in the monitor and the file), 0..1. */
export function noise(n: number): number {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}
